import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { IBankExport } from "entities/bankExport";
import { observer } from "mobx-react-lite";
import React from "react";
import { formatDateTime } from "utils/localizations";
import { busyIdentificator } from "utils/busyWatcher";
import ListView from "../../common/list";
import BankExportFilters from "./bankExportFilters";
import type BankExportListViewModel from "viewModels/finance/bankExports/bankExportListViewModel";
import BankTransferListViewModel from "viewModels/finance/bankExports/bankTransfers/bankTransferListViewModel";
import type TransferRefundExportResponse from "entities/transferRefundExportResponse";

export interface ExportListViewProps<TEntity extends IBankExport> {
  vm: BankExportListViewModel<TEntity>;
}

function BankExportListView<TEntity extends IBankExport>(props: ExportListViewProps<TEntity>) {
  const { vm } = props;
  const onItemClick = vm.onItemClick();

  const columns: ColumnDefinition<TEntity>[] = [
    {
      title: vm.translateAttribute("name"),
      property: "name",
      valueFormatter: props => (
        <Button variant="link" onClick={onItemClick && (() => onItemClick(props.item))} className="text-success px-0">
          {props.value}
        </Button>
      ),
    },
    {
      title: vm.translateAttribute("createdAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "createdAt",
    },
    {
      title: vm.translateAttribute("lastDownloadedBy"),
      property: "lastDownloadedBy",
    },
    {
      title: vm.translateAttribute("lastDownloadedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "lastDownloadedAt",
    },
  ];

  if (vm instanceof BankTransferListViewModel) {
    (columns as any as ColumnDefinition<TransferRefundExportResponse>[]).splice(1, 0, {
      title: vm.translateAttribute("refundType"),
      property: "refundType",
      valueFormatter: props => vm.translateState(props.value),
    });
  }

  const actionButtons = (item: TEntity) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));
    return (
      <Button
        variant="success"
        size="sm"
        className="rounded"
        onClick={() => vm.onDownload(item)}
        disabled={isBusy}
        iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
        {vm.translate("downloadFile")}
      </Button>
    );
  };

  return (
    <>
      <BankExportFilters vm={vm} />
      <ListView vm={vm} columns={columns} showDetailWithList={true} hideTitle={true} actionButtons={actionButtons} />
    </>
  );
}

export default observer(BankExportListView);
